import React, { useState } from 'react'
import {
  Alert, Box, Button, CircularProgress, TextField, Typography,
} from '@mui/material'
import { supabase } from '../../supabaseClient'

interface Props {
  onComplete: () => void
  /** Parent invite flow: a single-use token from /parent-setup?token=…. When
   *  present, we set the password server-side (no session yet) and sign in. */
  token?: string
}

export default function SetPasswordPage({ onComplete, token }: Props) {
  const [password,  setPassword]  = useState('')
  const [confirm,   setConfirm]   = useState('')
  const [loading,   setLoading]   = useState(false)
  const [error,     setError]     = useState<string | null>(null)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)

    if (password.length < 8) {
      setError('Password must be at least 8 characters.')
      return
    }
    if (password !== confirm) {
      setError('Passwords do not match.')
      return
    }

    setLoading(true)

    if (token) {
      // Parent invite: no session yet — set the password via our token, then
      // sign in with the credentials they just chose.
      const { data, error: invokeErr } = await supabase.functions.invoke('set-parent-password', {
        body: { token, password },
      })
      if (invokeErr || !data?.ok) {
        setLoading(false)
        setError(data?.error || invokeErr?.message || 'Could not set your password. Please try again.')
        return
      }
      const { error: signErr } = await supabase.auth.signInWithPassword({ email: data.email, password })
      setLoading(false)
      if (signErr) {
        setError(signErr.message)
        return
      }
      window.history.replaceState(null, '', '/')
      onComplete()
      return
    }

    const { error: updateErr } = await supabase.auth.updateUser({ password })
    setLoading(false)

    if (updateErr) {
      setError(updateErr.message)
      return
    }

    // Clear the invite hash so normal routing takes over
    window.history.replaceState(null, '', window.location.pathname)
    onComplete()
  }

  return (
    <Box sx={{
      minHeight:      '100vh',
      display:        'flex',
      alignItems:     'center',
      justifyContent: 'center',
      bgcolor:        'background.default',
      p:              3,
    }}>
      <Box sx={{ width: '100%', maxWidth: 400 }}>

        {/* Wordmark */}
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography sx={{ fontWeight: 800, fontSize: '1.25rem', color: 'primary.main', letterSpacing: '-0.01em', lineHeight: 1 }}>
            Ardent
          </Typography>
          <Typography sx={{ fontWeight: 700, fontSize: '0.6rem', color: 'secondary.main', letterSpacing: '0.14em', textTransform: 'uppercase' }}>
            Scholar
          </Typography>
        </Box>

        <Box sx={{
          bgcolor:      'background.paper',
          borderRadius: 3,
          border:       '1px solid',
          borderColor:  'divider',
          p:            4,
        }}>
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>
            Create your password
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.65 }}>
            Choose a password to secure your account. You'll use this to sign in going forward.
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Password"
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              fullWidth
              autoFocus
              autoComplete="new-password"
              helperText="At least 8 characters"
            />
            <TextField
              label="Confirm password"
              type="password"
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              required
              fullWidth
              autoComplete="new-password"
            />
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={loading}
              sx={{ mt: 1, py: 1.25 }}
            >
              {loading ? <CircularProgress size={22} color="inherit" /> : 'Set Password & Continue'}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
